import { Fragment } from 'react'
import { useParams, NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
// Models
import { IConversation } from 'app/models/ChatAI';
// Mui
import {
  Theme,
  ListItem, ListItemButton, ListItemText, ListItemIcon,
  Tooltip,
  Typography
} from '@mui/material';
// Icons
import { Sync as SyncIcon } from '@mui/icons-material';
// Components
import Icon from 'app/components/Icon';
// Hooks
import useAiChatPermissionResolver from 'app/hooks/useAiChatPermissionResolver';
import useCasePermissionResolved from 'app/hooks/useCasePermissionResolver';
// 
import ChatListItemDropdown from './ChatListItemDropdown';

type Props = {
  conversation: IConversation;
}

export default function ChatListItem({ conversation }:Props) {
  const { t } = useTranslation();

  const { caseId } = useParams<{ caseId:string }>()

  const { createdAutomatically, sharedByMe, sharedWithMe, allowModifications } = useAiChatPermissionResolver(conversation);
  const { isCaseProcessingReadyOrNotStarted } = useCasePermissionResolved();

  const tooltipTitle = ():string => {
    if (createdAutomatically) return t('pages.aiChat.tooltip.createdAutomatically');
    if (sharedByMe) return t('pages.aiChat.tooltip.sharedByMe');
    if (sharedWithMe) return t('pages.aiChat.tooltip.sharedWithMe');
    return t('pages.aiChat.tooltip.private');
  }

  const renderChatListDropdown = () => {
    if (!(isCaseProcessingReadyOrNotStarted && (allowModifications || createdAutomatically))) return null;
    return <ChatListItemDropdown conversation={conversation} />;
  }

  return (
    <Fragment>
      <ListItem
        secondaryAction={renderChatListDropdown()}
        disablePadding
      >
        <ListItemButton
          sx={{
            borderLeft: `4px solid transparent`,
            height: 48,
            pl: 3,
            '&.Mui-selected': {
              borderLeftColor: (theme: Theme) => theme.palette.primary.main,
              '& .MuiListItemIcon-root': {
                color: (theme: Theme) => theme.palette.primary.main
              }
            }
          }}
          component={NavLink}
          to={`/admin/cases/${caseId}/ai-chat/${conversation.id}`}
          activeClassName="Mui-selected"
        >
          <ListItemIcon sx={{ minWidth: 'initial' }}>
            <Tooltip title={tooltipTitle()} placement="right">
              <span>
                {createdAutomatically ? (
                  <Icon icon="chat_automated" />
                ) : sharedByMe ? (
                  <Icon icon="chat_shared_by_me" />
                ) : sharedWithMe ? (
                  <Icon icon="chat_shared_with_me" />
                ) : (
                  <Icon icon="chat-private" />
                )}
              </span>
            </Tooltip>
          </ListItemIcon>
          <ListItemText
            sx={{ pl: 4 }}
            primary={
              <Typography sx={{
                color: 'rgba(0, 0, 0, 0.87)',
                fontSize: '14px',
                fontWeight: 500,
              }} noWrap>{conversation.name}</Typography>
            }
          />
          {conversation.status === 'answering' && (
            <Tooltip title="Generating response" placement="right">
              <SyncIcon color="disabled" />
            </Tooltip>
          )}
        </ListItemButton>
      </ListItem>
    </Fragment>
  )
};
